import {
  RADAR_HOTELS_STATUS_CODE_PATH,
  RADAR_FLIGHTS_STATUS_CODE_PATH,
  RADAR_STATUS_CODE_PATH,
  ResultStatus,
} from '@web-platform/shared-universal-constants/src/radar';
import {
  RADAR_CREATE_SESSIONS,
  RADAR_POLL,
} from '@web-platform/shared-universal-types/src/network/requestTypes';
import { isInArray } from '@web-platform/shared-universal-utils-universal/src/is-in-array/is-in-array';
import get from 'lodash/get';

import type { ConductorPayloadResponseSuccess } from '@web-platform/shared-universal-types/src/network/banana/endpoints';
import type {
  RadarResponsePayload,
  RadarResponsePayloadSuccess,
  RadarResponsePayloadError,
} from '@web-platform/shared-universal-types/src/network/endpoints/radar/radarResponsePayloadTypes';
import type { MutationItem } from '@web-platform/shared-universal-types/src/redux/state/network/mutations';
import type { RadarQueryItem } from '@web-platform/shared-universal-types/src/redux/state/network/queries';

type RadarResponse = RadarResponsePayload;

export const isRadarResponse = (
  response: RadarResponsePayloadSuccess | ConductorPayloadResponseSuccess,
): response is RadarResponsePayloadSuccess =>
  !!response.body.itineraries && 'results' in response.body.itineraries;

export const getRadarFlightsResponseBody = (response: RadarResponsePayloadSuccess) =>
  response.body.itineraries;

export const isRadarFlightsResultEmptyWithNotModified = (response: RadarResponsePayloadSuccess) => {
  const status = get(response, RADAR_FLIGHTS_STATUS_CODE_PATH);

  const isNotModified = isInArray(status, [
    ResultStatus.INCOMPLETE_NOT_MODIFIED,
    ResultStatus.COMPLETE_NOT_MODIFIED,
  ]);

  const results = response.body.itineraries.results || [];
  return isNotModified && results.length === 0;
};

export function isRadarFlightsResponseError(
  response: RadarResponse,
): response is RadarResponsePayloadError {
  const status = get(response, RADAR_FLIGHTS_STATUS_CODE_PATH, ResultStatus.COMPLETE);

  return (
    !!response.error ||
    !isInArray(status, [
      ResultStatus.COMPLETE,
      ResultStatus.COMPLETE_NOT_MODIFIED,
      ResultStatus.INCOMPLETE,
      ResultStatus.INCOMPLETE_NOT_MODIFIED,
    ])
  );
}

const isResponseFailed = (statusCode: string) => statusCode === ResultStatus.FAILURE || !statusCode;

export function isMutationItemResponseANonRetryableError(mutation: MutationItem | void): boolean {
  if (!mutation || !mutation.response) {
    return false;
  }

  if (mutation.type === RADAR_CREATE_SESSIONS && mutation.response.status === 200) {
    const flightsStatusCode = get(mutation.response, RADAR_FLIGHTS_STATUS_CODE_PATH, '');
    const hotelsStatusCode = get(mutation.response, RADAR_HOTELS_STATUS_CODE_PATH, '');
    const unfocusedStatusCode = get(mutation.response, RADAR_STATUS_CODE_PATH, '');

    if (flightsStatusCode || hotelsStatusCode) {
      return isResponseFailed(flightsStatusCode) && isResponseFailed(hotelsStatusCode);
    }
    return isResponseFailed(unfocusedStatusCode);
  }

  return false;
}

export function isLastPollResponseANonRetryableError(query?: RadarQueryItem): boolean {
  if (!query || !query.lastResponse) {
    return false;
  }

  if (query.lastResponse.error) {
    return query.lastResponse.status === 404;
  }

  if (query.type === RADAR_POLL) {
    const flightsStatusCode = get(query.lastResponse, RADAR_FLIGHTS_STATUS_CODE_PATH);
    const hotelsStatusCode = get(query.lastResponse, RADAR_HOTELS_STATUS_CODE_PATH);

    return (
      (flightsStatusCode === ResultStatus.FAILURE || !flightsStatusCode) &&
      (hotelsStatusCode === ResultStatus.FAILURE || !hotelsStatusCode)
    );
  }

  return false;
}
